import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import type { T3ActivityResult } from '@shared/agent-protocol';
import type { RunStep } from '@shared/types/factory';
import { StatusBadge } from '@/components/StatusBadge';
import { formatRelative } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { AGENT_WAIT_STEP_TYPE } from './agent-activity';
import { AgentActivityFeed } from './AgentActivityFeed';
import { STEP_STATUS_META } from './run-status';
import { reviewStepSummary } from './review-badge';

/** The run whose agent the "Wait for the agent to finish" step opens (T-27). */
export interface RunStepAgentActivity {
  runId: string;
  /** The project's slug or id, for the request. */
  project?: string;
  /** The run is still going: the feed polls for new items. */
  live: boolean;
  /** Preview only (the /dev/ui gallery): shown instead of asking the machine. */
  preview?: T3ActivityResult;
}

export interface RunStepListProps {
  steps: RunStep[];
  /** Leave out each step's type (the task detail, where only the names matter). */
  compact?: boolean;
  /**
   * The run's T3 thread, when it has one: the agent's wait step can be expanded to show the agent at
   * work, read live from the machine.
   */
  agentActivity?: RunStepAgentActivity | null;
  className?: string;
}

/**
 * A run's steps in flow order: number, name, status, attempts, when, and a failed step's error. With
 * `agentActivity`, a started "Wait for the agent to finish" step opens the agent's activity feed.
 */
export function RunStepList({ steps, compact, agentActivity, className }: RunStepListProps) {
  const sorted = [...steps].sort((a, b) => a.position - b.position);
  return (
    <ol className={cn('divide-y rounded-md border', className)} aria-label="Steps">
      {sorted.map((step) => (
        <RunStepRow key={step.step_id} step={step} compact={compact} agentActivity={hasAgentActivity(step) ? agentActivity : null} />
      ))}
    </ol>
  );
}

const hasAgentActivity = (step: RunStep) => step.type === AGENT_WAIT_STEP_TYPE && step.status !== 'pending' && step.status !== 'skipped';

function RunStepRow({ step, compact, agentActivity }: { step: RunStep; compact?: boolean; agentActivity?: RunStepAgentActivity | null }) {
  const [open, setOpen] = useState(false);
  // The review stage's steps (T-36) say what they found or decided.
  const summary = reviewStepSummary(step);
  return (
    <li className={cn('space-y-1 px-3', compact ? 'py-1.5' : 'py-2')}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="w-5 text-right text-2xs text-muted-foreground tabular-nums">{step.position + 1}</span>
        <span className={cn('font-medium', compact && 'text-sm')}>{step.name ?? step.step_id}</span>
        {compact ? null : <span className="font-mono text-2xs text-muted-foreground">{step.type}</span>}
        <StatusBadge meta={STEP_STATUS_META[step.status]} size="sm" dot={step.status === 'running'} />
        {step.attempts > 1 ? <span className="text-2xs text-muted-foreground">{step.attempts} attempts</span> : null}
        <span className="ml-auto text-2xs text-muted-foreground">
          {step.finished_at ? formatRelative(step.finished_at) : step.started_at ? `Started ${formatRelative(step.started_at)}` : null}
        </span>
      </div>
      {step.error ? <p className="pl-8 text-xs text-danger-strong">{step.error}</p> : null}
      {summary ? <p className="pl-8 text-xs text-muted-foreground">{summary}</p> : null}
      {agentActivity ? (
        <div className="space-y-2 pl-8">
          <button
            type="button"
            className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} aria-hidden />
            {open ? "Hide the agent's work" : "Show the agent's work"}
          </button>
          {open ? (
            <AgentActivityFeed
              runId={agentActivity.runId}
              project={agentActivity.project}
              live={agentActivity.live}
              preview={agentActivity.preview}
              className="max-h-[32rem] overflow-y-auto rounded-md bg-muted/30 p-2"
            />
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
